import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme
} from '@mui/material';
import { 
  ExpandMore,
  Gavel,
  AccountCircle,
  Security,
  Copyright,
  Block,
  Payment,
  WarningAmber,
  Public,
  Bolt
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getTranslation } from '../../i18n';
import { Link } from 'react-router-dom';

const TermsPage = () => {
  const { language } = useSelector((state) => state.ui);
  const translations = getTranslation(language);
  const theme = useTheme();

  // Terms of service sections
  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: <Gavel color="primary" />,
      content: `By accessing or using SkyRadar's website, mobile application, and services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Services.

These Terms constitute a legally binding agreement between you and SkyRadar ("we," "us," or "our") regarding your use of the Services. Please read them carefully.`
    },
    {
      id: 'eligibility',
      title: 'Eligibility and Account Registration',
      icon: <AccountCircle color="primary" />,
      content: `To use certain features of our Services, you may need to register for an account. When you register, you agree to provide accurate, current, and complete information about yourself and to update this information to keep it accurate, current, and complete.

You are responsible for safeguarding your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.

You must be at least 18 years old to use our Services. By using our Services, you represent and warrant that you meet this requirement.`
    },
    {
      id: 'services',
      title: 'Description of Services',
      icon: <Bolt color="primary" />,
      content: `SkyRadar provides lightning detection and monitoring services, including but not limited to:

• Real-time lightning strike information displayed on interactive maps
• Historical lightning data and analytics
• Customizable alerts for lightning activity in specified areas
• API access for integration with other systems (for eligible subscription plans)

We reserve the right to modify, suspend, or discontinue any part of our Services at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of our Services.`
    },
    {
      id: 'subscription',
      title: 'Subscription Plans and Payment',
      icon: <Payment color="primary" />,
      content: `We offer various subscription plans for our Services. The features available to you depend on the subscription plan you choose.

When you purchase a subscription, you agree to pay the applicable fees as described at the time of purchase. All fees are non-refundable unless otherwise specified or required by applicable law.

Subscription fees are billed in advance on a recurring basis, depending on the billing cycle you select (e.g., monthly, annually). Your subscription will automatically renew at the end of each billing cycle unless you cancel it before the renewal date.

We may change our subscription fees at any time, but any changes will not affect your current subscription period. We will provide notice of any fee changes before they take effect.`
    },
    {
      id: 'user-conduct',
      title: 'User Conduct',
      icon: <Block color="primary" />,
      content: `You agree not to use our Services to:

• Violate any applicable law, regulation, or these Terms
• Infringe the rights of any third party, including intellectual property rights
• Harass, abuse, or harm another person
• Interfere with or disrupt our Services or servers
• Attempt to gain unauthorized access to our Services or systems
• Collect or store personal information about other users without their consent
• Impersonate any person or entity
• Engage in any fraudulent activity

We reserve the right to terminate or suspend your access to our Services immediately, without prior notice or liability, for any reason, including if you breach these Terms.`
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property Rights',
      icon: <Copyright color="primary" />,
      content: `All content, features, and functionality of our Services, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, are owned by SkyRadar, our licensors, or other providers of such material and are protected by copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.

These Terms do not grant you any right, title, or interest in our Services or any content, features, or functionality of our Services. You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Services, except as follows:

• Your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing those materials.
• You may store files that are automatically cached by your web browser for display enhancement purposes.
• You may print or download one copy of a reasonable number of pages of the website for your own personal, non-commercial use and not for further reproduction, publication, or distribution.

If we provide social media features with certain content, you may take such actions as are enabled by such features.`
    },
    {
      id: 'disclaimer',
      title: 'Disclaimer of Warranties',
      icon: <WarningAmber color="primary" />,
      content: `YOUR USE OF OUR SERVICES IS AT YOUR SOLE RISK. OUR SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.

SKYRADAR EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

SKYRADAR DOES NOT WARRANT THAT OUR SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE, OR THAT ANY DEFECTS IN OUR SERVICES WILL BE CORRECTED. SKYRADAR DOES NOT WARRANT THAT THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF OUR SERVICES WILL BE ACCURATE OR RELIABLE.

NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM SKYRADAR OR THROUGH OR FROM OUR SERVICES SHALL CREATE ANY WARRANTY NOT EXPRESSLY STATED IN THESE TERMS.

SKYRADAR SPECIFICALLY DISCLAIMS ANY RESPONSIBILITY FOR THE ACCURACY, COMPLETENESS, OR RELIABILITY OF LIGHTNING DETECTION DATA PROVIDED THROUGH OUR SERVICES. WHILE WE STRIVE TO PROVIDE ACCURATE INFORMATION, LIGHTNING DETECTION SYSTEMS HAVE INHERENT LIMITATIONS AND MAY NOT DETECT ALL LIGHTNING ACTIVITY.`
    },
    {
      id: 'limitation',
      title: 'Limitation of Liability',
      icon: <Security color="primary" />,
      content: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SKYRADAR SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:

• YOUR USE OR INABILITY TO USE OUR SERVICES
• ANY CHANGES MADE TO OUR SERVICES
• UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS OR DATA
• STATEMENTS OR CONDUCT OF ANY THIRD PARTY ON OUR SERVICES
• ANY OTHER MATTER RELATING TO OUR SERVICES

IN NO EVENT SHALL SKYRADAR'S TOTAL LIABILITY TO YOU FOR ALL DAMAGES, LOSSES, OR CAUSES OF ACTION EXCEED THE AMOUNT YOU HAVE PAID TO SKYRADAR IN THE LAST SIX (6) MONTHS, OR, IF GREATER, ONE HUNDRED DOLLARS ($100).

SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR THE LIMITATION OR EXCLUSION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, SOME OF THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU.`
    },
    {
      id: 'indemnification',
      title: 'Indemnification',
      icon: <Gavel color="primary" />,
      content: `You agree to defend, indemnify, and hold harmless SkyRadar, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of our Services, including, but not limited to, any use of our Services' content, services, and products other than as expressly authorized in these Terms.`
    },
    {
      id: 'governing-law',
      title: 'Governing Law and Jurisdiction',
      icon: <Public color="primary" />,
      content: `These Terms and any dispute or claim arising out of or in connection with them or their subject matter or formation (including non-contractual disputes or claims) shall be governed by and construed in accordance with the laws of the state of [State], without giving effect to any choice or conflict of law provision or rule.

Any legal suit, action, or proceeding arising out of, or related to, these Terms or our Services shall be instituted exclusively in the federal courts of the United States or the courts of the state of [State], although we retain the right to bring any suit, action, or proceeding against you for breach of these Terms in your country of residence or any other relevant country. You waive any and all objections to the exercise of jurisdiction over you by such courts and to venue in such courts.`
    },
    {
      id: 'changes',
      title: 'Changes to Terms',
      icon: <ExpandMore color="primary" />,
      content: `We may revise these Terms from time to time at our sole discretion. All changes are effective immediately when we post them, and apply to all access to and use of our Services thereafter.

Your continued use of our Services following the posting of revised Terms means that you accept and agree to the changes. You are expected to check this page frequently so you are aware of any changes, as they are binding on you.`
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Terms of Service
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Last Updated: June 1, 2023
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', mt: 2 }}>
          Please read these Terms of Service carefully before using SkyRadar's services.
          By accessing or using our services, you agree to be bound by these terms.
        </Typography>
      </Box>

      {/* Table of Contents */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Table of Contents
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {sections.map((section) => (
            <Button 
              key={section.id}
              variant="outlined" 
              size="small"
              href={`#${section.id}`}
              sx={{ 
                borderRadius: 4,
                textTransform: 'none',
                px: 2
              }}
            >
              {section.title}
            </Button>
          ))}
        </Box>
      </Paper>

      {/* Terms Sections as Accordions */}
      {sections.map((section) => (
        <Accordion 
          key={section.id} 
          id={section.id}
          sx={{ 
            mb: 2, 
            scrollMarginTop: '100px',
            '&:before': {
              display: 'none',
            },
            borderRadius: '8px !important',
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls={`${section.id}-content`}
            id={`${section.id}-header`}
            sx={{ 
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: 'white',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {section.icon}
              <Typography variant="h6" sx={{ ml: 2 }}>
                {section.title}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {section.content}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Footer */}
      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          By using our services, you acknowledge that you have read and understood these Terms of Service and agree to be bound by them.
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
          {translations.landing.footer.email}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            component={Link}
            to="/privacy"
          >
            Privacy Policy
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            component={Link}
            to="/contact"
          >
            Contact Us
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default TermsPage;
